import type { ReactNode } from "react";
import { useMemo } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import useHousehold from "src/contexts/household-context";

import MessageHeartSquareIcon from "src/icons/untitled-ui/duocolor/message-heart-square";
import MedicalCircleIcon from "src/icons/untitled-ui/duocolor/medical-circle";
import BankIcon from "src/icons/untitled-ui/duocolor/bank";
import FilePlus02Icon from "src/icons/untitled-ui/duocolor/file-plus-02";
import Phone01Icon from "src/icons/untitled-ui/duocolor/phone-01";
import FileHeart02Icon from "src/icons/untitled-ui/duocolor/file-heart-02";
import FileShield02Icon from "src/icons/untitled-ui/duocolor/file-shield-02";
import UsersPlusIcon from "src/icons/untitled-ui/duocolor/users-plus";
import Users03Icon from "src/icons/untitled-ui/duocolor/users-03";
import Home03Icon from "src/icons/untitled-ui/duocolor/home-03";
import BoxIcon from "src/icons/untitled-ui/duocolor/box";
import { paths } from "src/paths";
import HomeSmile from "src/icons/untitled-ui/duocolor/home-smile";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

export const useSections = () => {
  const { selectedHousehold } = useHousehold();

  const hOH = selectedHousehold?.head_of_household;
  const estate_plan_foundation = hOH?.estate_plan_foundation;

  const isJoint = (estate_plan_foundation === "joint" || estate_plan_foundation === "attorney");
  const head = selectedHousehold?.head_of_household;

  //------------------------------------------------------------------
  // 🔤  Name helpers
  //------------------------------------------------------------------
  const splitName = (full?: string | null) => {
    if (!full) return { first: "Unknown", last: "" };
    const parts = full.trim().split(/\s+/);
    return { first: parts[0], last: parts[parts.length - 1] };
  };

  const headName   = splitName(head?.full_name);
  const spouseName = splitName(
    (selectedHousehold?.household_member_roles ?? [])
      .find((m) => m.role === "SPOUSE")
      ?.role_target_user_data?.full_name
  );

  // Build the joint label
  let jointTitle: string;
  if (!spouseName.last) {
    // no spouse – fallback
    jointTitle = `${headName.first} ${headName.last}`;
  } else if (
    headName.last.toLowerCase() === spouseName.last.toLowerCase()
  ) {
    // same last name → “Ian & Kanitha Snow”
    jointTitle = `${headName.first} & ${spouseName.first} ${headName.last}`;
  } else {
    // different last names → “Ian & Kanitha”
    jointTitle = `${headName.first} & ${spouseName.first}`;
  }

  return useMemo(
    () => {
      return [
        {
          items: [
            {
              title: "Home",
              path: paths.dashboard.index,
              icon: (
                <SvgIcon fontSize="small">
                  <HomeSmile />
                </SvgIcon>
              ),
            },
            {
              title: "Sharing",
              path: paths.dashboard.accountSharing("sharing"),
              icon: (
                <SvgIcon fontSize="small">
                  <UsersPlusIcon />
                </SvgIcon>
              ),
            },
          ],
        },
        {
          subheader: "Essentials",
          items: [
            {
              title: "Wills & Trusts",
              icon: (
                <SvgIcon fontSize="small">
                  <FileShield02Icon />
                </SvgIcon>
              ),
              items: [
                    {
                      title: `${isJoint ? jointTitle : head?.full_name ?? "Unknown"}`,
                      path: `/${selectedHousehold?.head_of_household?.ud_id}/wills-and-trusts`,
                    },
                    ...(isJoint
                      ? [] // skip spouse list
                      : (selectedHousehold?.household_member_roles ?? [])
                          .filter((m) => m.role === "SPOUSE")
                          .map((m) => ({
                            title: m.role_target_user_data?.full_name ?? "Unknown",
                            path: `/${m.role_target_user_data_ud_id}/wills-and-trusts`,
                          }))
                    ),
                  ],
            },            
            {
              title: "Power of Attorney",
              icon: (
                <SvgIcon fontSize="small">
                  <FilePlus02Icon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/financial-power-of-attorney`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/financial-power-of-attorney`,
                })) || [])
              ],
            },
            {
              title: "Health Care Directive",
              icon: (
                <SvgIcon fontSize="small">
                  <FileHeart02Icon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/health-care-directive`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/health-care-directive`,
                })) || [])
              ],
            },
          ],
        },
        {
          subheader: "Total Planning",
          items: [

            {
              title: "Personal",
              icon: (
                <SvgIcon fontSize="small">
                  <Users03Icon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/personal`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/personal`,
                })) || [])
              ],
            },
            {
              title: "Legal",
              icon: (
                <SvgIcon fontSize="small">
                  <BoxIcon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/legal`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/legal`,
                })) || [])
              ],
            },
            {
              title: "Medical",
              icon: (
                <SvgIcon fontSize="small">
                  <MedicalCircleIcon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/medical`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/medical`,
                })) || [])
              ],
            },
            {
              title: "Financial",
              icon: (
                <SvgIcon fontSize="small">
                  <BankIcon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/financial`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/financial`,
                })) || [])
              ],
            },
            {
              title: "Property",
              icon: (
                <SvgIcon fontSize="small">
                  <Home03Icon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/property`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/property`,
                })) || [])
              ],
            },
            {
              title: "Digital",
              icon: (
                <SvgIcon fontSize="small">
                  <Phone01Icon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/digital`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/digital`,
                })) || [])
              ],
            },
            {
              title: "Legacy",
              icon: (
                <SvgIcon fontSize="small">
                  <MessageHeartSquareIcon />
                </SvgIcon>
              ),
              items: [
                // Fixed item at the top
                {
                  title: `${selectedHousehold?.head_of_household?.full_name}`,
                  path: `/${selectedHousehold?.head_of_household?.ud_id}/legacy`,
                },
                // Dynamically mapped members
                ...(selectedHousehold?.household_member_roles.filter((member) => member.is_household_member).map((member) => ({
                  title: member?.role_target_user_data?.full_name || "Unknown",
                  path: `/${member?.role_target_user_data_ud_id}/legacy`,
                })) || [])
              ],
            },
          ],
        },
      ];
    },
    [
      selectedHousehold?.household_member_roles, 
      head?.full_name,
      isJoint,
      jointTitle,
      selectedHousehold?.head_of_household?.full_name,
      selectedHousehold?.head_of_household?.ud_id
    ]
  );
};